import '../styles/pdf-pages.css';
import '../styles/model-overview.css';

import { selectIsAuthenticated } from '@/domain/auth/auth.selectors';
import { CalculateTourCo2 } from '@/domain/impact-calculate-tour/entities/calculate-tour-co2.entity';
import { selectImpactCalculateTourCo2ForProject } from '@/domain/impact-calculate-tour/impact-calculate-tour.selectors';
import { ProjectTour } from '@/domain/project-tour/entities/project-tour.entity';
import { selectCurrentProjectTour } from '@/domain/project-tour/project-tour.selectors';
import { useAppSelector } from '@/ui/hook/store';
import { translate } from '@/ui/i18n';
import { formatProjectDatesTz } from '@/ui/utils/format-project-dates';
import { getColorScaleForScore } from '@/ui/utils/get-color-for-score';

import CalendarMonthSvg from '../assets/calendar_month.svg';
import { Co2Infography } from '../components/Co2Infography';
import { PdfProjectPager } from '../components/PdfProjectPager';
import { PdfScoreCard } from '../components/PdfScoreCard';
import { displayDecimal } from '../utils/display-scores.utils';

export function PdfTourPage2({ totalPages }: { totalPages: number }) {
  const isAuthenticated = useAppSelector(selectIsAuthenticated);
  const tour = useAppSelector(
    selectCurrentProjectTour,
  ) satisfies ProjectTour | null;
  const tourId = tour?.id ?? 0;

  const impactCo2 = useAppSelector(
    selectImpactCalculateTourCo2ForProject(tourId),
  ) satisfies CalculateTourCo2;

  if (!isAuthenticated) {
    return <h1>{translate('PdfExports.user-not-authentified')}</h1>;
  }

  if (!tour) {
    return <h1>{translate('PdfExports.tour-not-found')}</h1>;
  }

  const general = impactCo2.general;

  return (
    <div id="page2" className="pdf-page">
      <div className="content">
        <div className="pdf-light-header">
          <div>
            <h1>{tour.name}</h1>
            {tour.startDate && tour.endDate && (
              <h2>
                <img
                  src={CalendarMonthSvg}
                  width="19"
                  height="19"
                  style={{ position: 'relative', top: 4, marginRight: 5 }}
                  alt="Calendar icon"
                />
                <span className="tour-date">
                  {formatProjectDatesTz(tour.startDate, tour.endDate)}
                </span>
              </h2>
            )}
          </div>
        </div>
        <div className="pdf-separator"></div>

        <div className="model-header">
          <div>
            <h2>{translate('PdfExports.co2-score-details')}</h2>
            <h5>{translate('ProjectsTour.project-tour-general')}</h5>
          </div>
          <div>
            <h4>
              <span className="highlight">
                {displayDecimal(general.score)}{' '}
              </span>
              {translate('Projects.teCO2')}
            </h4>
          </div>
          <div>
            <PdfScoreCard type="co2" scoreLetter="-" size="medium" />
          </div>
        </div>

        <div className="model-content">
          <div
            style={{
              width: 350,
              display: 'flex',
              flexDirection: 'column',
              gap: '20px',
            }}
          >
            <div className="sector">
              <div className="sector-header">
                <h4>
                  {translate('ProjectsTour.GeneralCo2Quiz.Sections.CREATION')}
                </h4>
                <div>
                  <span className="highlight">
                    {displayDecimal(general.sections.CREATION.score)}
                  </span>{' '}
                  {translate('Projects.teCO2')}
                </div>
              </div>
              <div className="sector-value">
                <ul>
                  <li>
                    <span>{translate('Co2.Sections.SCENOGRAPHY')}</span>
                    <span>
                      {displayDecimal(
                        general.sections.CREATION.categories.SCENOGRAPHY.score,
                      )}{' '}
                      {translate('Projects.teCO2')}
                    </span>
                  </li>
                  <li>
                    <span>
                      {translate(
                        'ProjectsTour.GeneralCo2Quiz.Categories.TEAM_MOBILITY',
                      )}
                    </span>
                    <span>
                      {displayDecimal(
                        general.sections.CREATION.categories.TEAM_MOBILITY
                          .score,
                      )}{' '}
                      {translate('Projects.teCO2')}
                    </span>
                  </li>
                  <li>
                    <span>
                      {translate(
                        'ProjectsTour.GeneralCo2Quiz.Categories.REPEATS_AND_MATERIAL',
                      )}
                    </span>
                    <span>
                      {displayDecimal(
                        general.sections.CREATION.categories
                          .REPEATS_AND_MATERIAL.score,
                      )}{' '}
                      {translate('Projects.teCO2')}
                    </span>
                  </li>
                </ul>
              </div>
            </div>

            <div className="sector">
              <div className="sector-header">
                <h4>
                  {translate('ProjectsTour.GeneralCo2Quiz.Sections.MANAGEMENT')}
                </h4>
                <div>
                  <span className="highlight">
                    {displayDecimal(general.sections.MANAGEMENT.score)}
                  </span>{' '}
                  {translate('Projects.teCO2')}
                </div>
              </div>
              <div className="sector-value">
                <ul>
                  <li>
                    <span>
                      {translate(
                        'ProjectsTour.GeneralCo2Quiz.Categories.TEAM_MOBILITY',
                      )}
                    </span>
                    <span>
                      {displayDecimal(
                        general.sections.MANAGEMENT.categories.TEAM_MOBILITY
                          .score,
                      )}{' '}
                      {translate('Projects.teCO2')}
                    </span>
                  </li>
                  <li>
                    <span>{translate('Co2.Sections.CATERING')}</span>
                    <span>
                      {displayDecimal(
                        general.sections.MANAGEMENT.categories.CATERING.score,
                      )}{' '}
                      {translate('Projects.teCO2')}
                    </span>
                  </li>
                  <li>
                    <span>
                      {translate(
                        'ProjectsTour.GeneralCo2Quiz.Categories.OFFICE',
                      )}
                    </span>
                    <span>
                      {displayDecimal(
                        general.sections.MANAGEMENT.categories.OFFICE.score,
                      )}{' '}
                      {translate('Projects.teCO2')}
                    </span>
                  </li>
                </ul>
              </div>
            </div>

            <div className="sector">
              <div className="sector-header">
                <h4>
                  {translate(
                    'ProjectsTour.GeneralCo2Quiz.Sections.COMMUNICATION',
                  )}
                </h4>
                <div>
                  <span className="highlight">
                    {displayDecimal(general.sections.COMMUNICATION.score)}
                  </span>{' '}
                  {translate('Projects.teCO2')}
                </div>
              </div>
              <div className="sector-value">
                <ul>
                  <li>
                    <span>
                      {translate(
                        'ProjectsTour.GeneralCo2Quiz.Categories.VIDEO_AND_DIGITAL',
                      )}
                    </span>
                    <span>
                      {displayDecimal(
                        general.sections.COMMUNICATION.categories
                          .VIDEO_AND_DIGITAL.score,
                      )}{' '}
                      {translate('Projects.teCO2')}
                    </span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
          <div
            style={{
              width: 350,
              display: 'flex',
              flexDirection: 'column',
              gap: '20px',
            }}
          >
            <Co2Infography
              items={[
                {
                  score: general.sections.CREATION.score,
                  label: translate(
                    'ProjectsTour.GeneralCo2Quiz.Sections.CREATION',
                  ),
                },
                {
                  score: general.sections.MANAGEMENT.score,
                  label: translate(
                    'ProjectsTour.GeneralCo2Quiz.Sections.MANAGEMENT',
                  ),
                },
                {
                  score: general.sections.COMMUNICATION.score,
                  label: translate(
                    'ProjectsTour.GeneralCo2Quiz.Sections.COMMUNICATION',
                  ),
                },
              ]}
              colorScale={getColorScaleForScore('-')}
              title={translate('PdfExports.co2-general-emissions')}
            />
            <div className="model-overall-comments">
              {translate('PdfExports.comment')}
            </div>
          </div>
        </div>

        <div className="navigation-controls">
          <a href="#page1" className="tag-pill">
            {translate('PdfExports.go-back-to-overview')}
          </a>
          <a href="#page3" className="tag-pill">
            {translate('PdfExports.go-to-co2-ontour')}
          </a>
        </div>
      </div>

      <PdfProjectPager current={2} total={totalPages}></PdfProjectPager>
    </div>
  );
}
